import { create } from "zustand";

export const useAuditEvaluationStore = create((set, get) => ({
  myAudits: [],
  loading: false,
  error: null,
  searchQuery: "",
  sortBy: null,
  sortDirection: null,
  pagination: {
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  columnFilters: {},
  uniqueValues: {},

  evaluationOpen: false,
  evaluationLoading: false,
  currentAudit: null,
  questionDocs: [],
  evaluation: null,
  controls: [],
  controlAnswers: {},

  allEvaluations: [],
  selectedAuditorId: 'all',
  selectedUnit: 'all',
  unitList: [],
  unitControlMap: {},

  // Setters
  setMyAudits: (myAudits) => set({ myAudits }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setSort: (sortBy, sortDirection) => set({ sortBy, sortDirection }),
  setPagination: (pagination) => set(state => ({ pagination: { ...state.pagination, ...pagination } })),
  setColumnFilters: (columnFilters) => set({ columnFilters }),
  setUniqueValues: (uniqueValues) => set({ uniqueValues }),

  setEvaluationOpen: (isOpen) => set({ evaluationOpen: isOpen }),
  setEvaluationLoading: (loading) => set({ evaluationLoading: loading }),
  setCurrentAudit: (audit) => set({ currentAudit: audit }),
  setQuestionDocs: (questionDocs) => set({ questionDocs }),
  setEvaluation: (evaluation) => set({ evaluation }),
  setControls: (controls) => set({ controls }),
  setControlAnswers: (controlAnswers) => set({ controlAnswers }),
  setAllEvaluations: (allEvaluations) => set({ allEvaluations }),
  setSelectedAuditorId: (selectedAuditorId) => set({ selectedAuditorId }),
  setSelectedUnit: (selectedUnit) => set({ selectedUnit }),
  setUnitList: (unitList) => set({ unitList }),
  setUnitControlMap: (unitControlMap) => set({ unitControlMap }),

  closeEvaluation: () =>
    set({
      evaluationOpen: false,
      evaluationLoading: false,
      currentAudit: null,
      questionDocs: [],
      evaluation: null,
      controls: [],
      controlAnswers: {},
      allEvaluations: [],
      selectedAuditorId: 'all',
      selectedUnit: 'all',
      unitList: [],
      unitControlMap: {},
    }),

  updateControlAnswer: (controlId, patch) => {
    if (!controlId) return;
    set((state) => {
      const prev = state.controlAnswers[controlId] || {};
      return {
        controlAnswers: {
          ...state.controlAnswers,
          [controlId]: { ...prev, ...patch },
        },
      };
    });
  },

  addControlEvidence: (controlId, evidence) => {
    if (!controlId || !evidence) return;
    set((state) => {
      const prev = state.controlAnswers[controlId] || {};
      const prevEvidences = Array.isArray(prev.evidences) ? prev.evidences : [];
      return {
        controlAnswers: {
          ...state.controlAnswers,
          [controlId]: { ...prev, evidences: [...prevEvidences, evidence] },
        },
      };
    });
  },

  removeControlEvidence: (controlId, evidenceId) => {
    if (!controlId || !evidenceId) return;
    set((state) => {
      const prev = state.controlAnswers[controlId] || {};
      const prevEvidences = Array.isArray(prev.evidences) ? prev.evidences : [];
      return {
        controlAnswers: {
          ...state.controlAnswers,
          [controlId]: { ...prev, evidences: prevEvidences.filter(ev => ev.id !== evidenceId) },
        },
      };
    });
  },

  updateAnswer: (questionId, patch) => {
    if (!questionId) return;
    set((state) => {
      const prevEval = state.evaluation || {};
      const prevAnswers = prevEval.answers || {};
      const prev = prevAnswers[questionId] || { result: "NotAssessed", comment: "", evidences: [] };
      const next = { ...prev, ...patch };
      return {
        evaluation: {
          ...prevEval,
          answers: {
            ...prevAnswers,
            [questionId]: next,
          },
        },
      };
    });
  },

  addEvidence: (questionId, evidence) => {
    if (!questionId || !evidence) return;
    set((state) => {
      const prevEval = state.evaluation || {};
      const prevAnswers = prevEval.answers || {};
      const prev = prevAnswers[questionId] || { result: "NotAssessed", comment: "", evidences: [] };
      const nextEvidences = Array.isArray(prev.evidences) ? [...prev.evidences, evidence] : [evidence];
      return {
        evaluation: {
          ...prevEval,
          answers: {
            ...prevAnswers,
            [questionId]: {
              ...prev,
              evidences: nextEvidences,
            },
          },
        },
      };
    });
  },

  removeEvidence: (questionId, evidenceId) => {
    if (!questionId || !evidenceId) return;
    set((state) => {
      const prevEval = state.evaluation || {};
      const prevAnswers = prevEval.answers || {};
      const prev = prevAnswers[questionId] || { result: "NotAssessed", comment: "", evidences: [] };
      const nextEvidences = (Array.isArray(prev.evidences) ? prev.evidences : []).filter(
        (ev) => ev.id !== evidenceId,
      );
      return {
        evaluation: {
          ...prevEval,
          answers: {
            ...prevAnswers,
            [questionId]: {
              ...prev,
              evidences: nextEvidences,
            },
          },
        },
      };
    });
  },
}));
